import React from 'react';
import { Link } from 'react-router-dom';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-sm rounded-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
            <p className="text-sm text-gray-600">Last updated: July 28, 2025</p>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using Workazon ("Service"), you accept and agree to be bound by the terms 
              and provision of this agreement. If you do not agree to abide by the above, please do not 
              use this service.
            </p>

            <h2>2. Description of Service</h2>
            <p>
              Workazon is an educational and career platform that connects students with employers through 
              skills-based learning, AI-validated assessments, and direct job matching. Our services include:
            </p>
            <ul>
              <li>Online courses and educational content</li>
              <li>Skills assessments and certifications</li>
              <li>Job postings and application management</li>
              <li>Career matching and recommendations</li>
              <li>Background verification services</li>
              <li>Real-time rankings and performance tracking</li>
            </ul>

            <h2>3. User Accounts and Registration</h2>
            <p>
              To access certain features of the Service, you must register for an account. You agree to:
            </p>
            <ul>
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>

            <h2>4. User Conduct and Responsibilities</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Upload, post, or transmit any content that is unlawful, harmful, threatening, abusive, 
                  harassing, defamatory, vulgar, obscene, or otherwise objectionable</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Upload or transmit any content that infringes intellectual property rights</li>
              <li>Engage in any activity that interferes with or disrupts the Service</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Use automated systems to access the Service without permission</li>
            </ul>

            <h2>5. Educational Content and Assessments</h2>
            <p>
              Our educational content and AI-powered assessments are designed to evaluate and improve your skills. 
              By participating, you acknowledge that:
            </p>
            <ul>
              <li>Assessment results are based on AI analysis and may not be 100% accurate</li>
              <li>Certifications are provided by Workazon and may not be recognized by all institutions</li>
              <li>You are responsible for the accuracy of your submissions and assessment responses</li>
              <li>Cheating or fraudulent behavior will result in account termination</li>
            </ul>

            <h2>6. Employment and Job Matching</h2>
            <p>
              Workazon facilitates connections between job seekers and employers but does not guarantee 
              employment. We are not responsible for:
            </p>
            <ul>
              <li>The accuracy of job postings or employer information</li>
              <li>Employment decisions made by employers</li>
              <li>Disputes between users and employers</li>
              <li>The quality or safety of work environments</li>
            </ul>

            <h2>7. Subscription and Payments</h2>
            <p>
              Certain features may require paid subscriptions. By subscribing, you agree to:
            </p>
            <ul>
              <li>Pay all applicable fees as described in your subscription plan</li>
              <li>Automatic renewal unless cancelled before the renewal date</li>
              <li>Our refund policy as stated in your subscription terms</li>
              <li>Potential changes to subscription fees with 30 days notice</li>
            </ul>

            <h2>8. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Please review our Privacy Policy, which explains how we 
              collect, use, and protect your information. By using our Service, you consent to our 
              data practices as described in the Privacy Policy.
            </p>

            <h2>9. Intellectual Property Rights</h2>
            <p>
              The Service and its original content, features, and functionality are owned by Workazon 
              and are protected by international copyright, trademark, patent, trade secret, and other 
              intellectual property laws.
            </p>

            <h2>10. Background Checks and Verification</h2>
            <p>
              We may offer background verification services. By using these services, you:
            </p>
            <ul>
              <li>Consent to the collection and verification of your information</li>
              <li>Acknowledge that verification results may be shared with potential employers</li>
              <li>Understand that background checks are conducted by third-party providers</li>
              <li>Agree to the accuracy of information you provide for verification</li>
            </ul>

            <h2>11. Termination</h2>
            <p>
              We may terminate or suspend your account and access to the Service immediately, without 
              prior notice, for conduct that we believe violates these Terms or is harmful to other 
              users, us, or third parties.
            </p>

            <h2>12. Disclaimers and Limitation of Liability</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE 
              THE ACCURACY, COMPLETENESS, OR USEFULNESS OF ANY INFORMATION ON THE SERVICE.
            </p>
            <p>
              IN NO EVENT SHALL WORKAZON BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, 
              OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF THE SERVICE.
            </p>

            <h2>13. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Workazon from any claims, damages, or expenses 
              arising from your use of the Service or violation of these Terms.
            </p>

            <h2>14. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United 
              States, without regard to conflict of law principles.
            </p>

            <h2>15. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify users of significant 
              changes via email or through the Service. Continued use after changes constitutes acceptance 
              of the new Terms.
            </p>

            <h2>16. Contact Information</h2>
            <p>
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p><strong>Email:</strong> legal@workazon.com</p>
              <p><strong>Address:</strong> Workazon Legal Department</p>
              <p className="text-sm text-gray-600 mt-2">
                For support inquiries, please visit our <Link to="/support" className="text-primary-600 hover:text-primary-700">Support Page</Link>
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <Link 
              to="/" 
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
            >
              Return to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;